/**
 * DeliveryTypeSelect — dropdown de tipo de entrega com ícone colorido + texto.
 *
 * Select nativo não renderiza ícone dentro de <option>, então isto é um
 * dropdown customizado (mesmo padrão usado em CampaignModal/StatsModal).
 * Reutilizado por PlanManager (entrega por plano), DefaultDeliveryCard
 * (entrega padrão do bot) e Downsell (global + por oferta).
 *
 * Props:
 * - value: string (o `value` de uma das options)
 * - onChange: (value) => void
 * - options: [{ value, label, icon: LucideComponent, colorClass, disabled?, hint? }]
 *   (disabled = aparece apagada, com `hint` explicando, e não pode ser escolhida)
 */

import { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';

export default function DeliveryTypeSelect({ value, onChange, options, className = '' }) {
    const [open, setOpen] = useState(false);
    const rootRef = useRef(null);

    useEffect(() => {
        function onClickOutside(e) {
            if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
        }
        document.addEventListener('mousedown', onClickOutside);
        return () => document.removeEventListener('mousedown', onClickOutside);
    }, []);

    const selected = options.find((o) => o.value === value) || options[0];
    const SelectedIcon = selected?.icon;

    return (
        <div className={`relative ${className}`} ref={rootRef}>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl bg-white/[0.03] border border-white/[0.08] text-sm text-white outline-none hover:border-white/20 transition"
            >
                <span className="flex items-center gap-2 min-w-0 truncate">
                    {SelectedIcon && <SelectedIcon size={15} className={`shrink-0 ${selected.colorClass || 'text-white/50'}`} />}
                    <span className="truncate">{selected?.label}</span>
                </span>
                <ChevronDown size={16} className={`text-white/40 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>

            {open && (
                <div className="absolute z-10 mt-1.5 w-full rounded-xl border border-white/[0.1] bg-[#161b22] shadow-[0_16px_40px_rgba(0,0,0,0.5)] overflow-hidden py-1">
                    {options.map((o) => (
                        <button
                            key={o.value}
                            type="button"
                            disabled={o.disabled}
                            title={o.disabled ? o.hint : undefined}
                            onClick={() => { if (o.disabled) return; onChange(o.value); setOpen(false); }}
                            className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-left transition ${o.disabled ? 'opacity-40 cursor-not-allowed text-white/60' : value === o.value ? 'bg-[rgba(139,92,246,0.18)] text-white' : 'text-white/80 hover:bg-white/[0.06]'}`}
                        >
                            <o.icon size={15} className={`shrink-0 ${o.colorClass || 'text-white/50'}`} />
                            <span className="min-w-0 flex flex-col">
                                <span className="truncate">{o.label}</span>
                                {o.disabled && o.hint && <span className="text-[11px] text-white/50 truncate">{o.hint}</span>}
                            </span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
